import { useState, useEffect } from "react";
import { useAuth } from "../../auth/AuthContext";
import { uploadImage } from "../../services/cloudinary";
import { useNavigate } from "react-router-dom";
import styled from "styled-components";
import { apiRequest } from "../../services/api";

const presetAvatars = [
  "https://api.dicebear.com/7.x/adventurer/svg?seed=Lucas",
  "https://api.dicebear.com/7.x/adventurer/svg?seed=Maria",
  "https://api.dicebear.com/7.x/adventurer/svg?seed=Leo",
  "https://api.dicebear.com/7.x/adventurer/svg?seed=Anna",
  "https://api.dicebear.com/7.x/adventurer/svg?seed=Tito",
  "https://api.dicebear.com/7.x/adventurer/svg?seed=Mari",
  "https://api.dicebear.com/7.x/adventurer/svg?seed=Toto",
  "https://api.dicebear.com/7.x/adventurer/svg?seed=Lala",
  "https://api.dicebear.com/7.x/adventurer/svg?seed=Nina",
  "https://api.dicebear.com/7.x/adventurer/svg?seed=kiki",
  "https://api.dicebear.com/7.x/adventurer/svg?seed=koko",
  "https://api.dicebear.com/7.x/adventurer/svg?seed=tata",
  "https://api.dicebear.com/7.x/adventurer/svg?seed=pupu",
];

const PerfilAjustes = () => {
  const { user, setUser } = useAuth();
  const navigate = useNavigate();

  /* ================= FORM STATE LIMPIO ================= */

  const [form, setForm] = useState({
    name: "",
    lastName: "",
    phone: "",
    city: "",
    allowWhatsapp: false,
    avatar: "",
    professionalData: {
      companyName: "",
      description: "",
      yearsExperience: "",
      license: "",
      licenseJurisdiction: "",
      specialties: "",
      serviceAreas: "",
      phone: "",
      address: "",
      city: "",
      province: "",
      website: "",
      instagram: "",
      logo: "",
      cover: "",
      businessHours: "",
      showAddress: false,
      showPhone: true,
    },
  });

  const [loading, setLoading] = useState(false);
  const [profileFeedback, setProfileFeedback] = useState("");
  const [uploading, setUploading] = useState(false);
  const [professionalUploading, setProfessionalUploading] = useState("");
  const [sessions, setSessions] = useState([]);
  const [sessionFeedback, setSessionFeedback] = useState("");
  const [deletionStep, setDeletionStep] = useState("idle");
  const [deletionCode, setDeletionCode] = useState("");
  const [deletionPhrase, setDeletionPhrase] = useState("");
  const [deletionFeedback, setDeletionFeedback] = useState("");

  /* ================= SINCRONIZAR CUANDO USER CAMBIE ================= */

  useEffect(() => {
    if (!user) return;

    setForm({
      name: user?.name || "",
      lastName: user?.lastName || "",
      phone: user?.phone || "",
      city: user?.city || "",
      allowWhatsapp: user?.allowWhatsapp || false,
      avatar: user?.avatar || "",
      professionalData: {
        companyName: user?.professionalData?.companyName || "",
        description: user?.professionalData?.description || "",
        yearsExperience: user?.professionalData?.yearsExperience ?? "",
        license: user?.professionalData?.license || "",
        licenseJurisdiction: user?.professionalData?.licenseJurisdiction || "",
        specialties: (user?.professionalData?.specialties || []).join(", "),
        serviceAreas: (user?.professionalData?.serviceAreas || []).join(", "),
        phone: user?.professionalData?.phone || "",
        address: user?.professionalData?.address || "",
        city: user?.professionalData?.city || "",
        province: user?.professionalData?.province || "",
        website: user?.professionalData?.website || "",
        instagram: user?.professionalData?.instagram || "",
        logo: user?.professionalData?.logo || "",
        cover: user?.professionalData?.cover || "",
        businessHours: user?.professionalData?.businessHours || "",
        showAddress: user?.professionalData?.showAddress || false,
        showPhone: user?.professionalData?.showPhone !== false,
      },
    });
  }, [user]);

  const loadSessions = async () => {
    try {
      const data = await apiRequest('/api/auth/sessions');
      setSessions(data.sessions || []);
    } catch (error) {
      setSessionFeedback(error.message || 'No pudimos cargar tus dispositivos.');
    }
  };

  useEffect(() => {
    if (user) loadSessions();
  }, [user]);

  const closeSession = async (sessionId) => {
    setSessionFeedback('Cerrando sesión…');
    try {
      await apiRequest(`/api/auth/sessions/${sessionId}`, { method: 'DELETE' });
      setSessions((current) => current.filter((session) => session.id !== sessionId));
      setSessionFeedback('La sesión fue cerrada.');
    } catch (error) {
      setSessionFeedback(error.message || 'No pudimos cerrar esa sesión.');
    }
  };

  const closeOtherSessions = async () => {
    setSessionFeedback('Protegiendo tu cuenta…');
    try {
      await apiRequest('/api/auth/sessions/others', { method: 'DELETE' });
      setSessions((current) => current.filter((session) => session.current));
      setSessionFeedback('Las demás sesiones fueron cerradas.');
    } catch (error) {
      setSessionFeedback(error.message || 'No pudimos cerrar las demás sesiones.');
    }
  };

  const requestDeletion = async () => {
    setDeletionFeedback("Enviando confirmación…");
    try {
      const data = await apiRequest('/api/users/me/deletion-request', { method: 'POST' });
      setDeletionStep("confirm");
      setDeletionFeedback(data.message);
    } catch (error) {
      setDeletionFeedback(error.message || "No pudimos iniciar la baja.");
    }
  };

  const confirmDeletion = async () => {
    if (deletionPhrase !== "ELIMINAR") return setDeletionFeedback('Escribí ELIMINAR para confirmar.');
    setDeletionStep("deleting");
    setDeletionFeedback("Eliminando tu cuenta y sus datos…");
    try {
      await apiRequest('/api/users/me/deletion-confirm', {
        method: 'POST',
        body: { code: deletionCode.trim().toUpperCase() },
      });
      localStorage.removeItem('verifyEmail');
      setUser(null);
      navigate('/', { replace: true });
    } catch (error) {
      setDeletionStep("confirm");
      setDeletionFeedback(error.message || "No pudimos completar la baja.");
    }
  };

  /* ================= AVATAR ================= */

  const initials =
    form.name && form.lastName
      ? `${form.name[0]}${form.lastName[0]}`.toUpperCase()
      : "U";

  const gradientColors = [
    "linear-gradient(135deg,#c66f4e,#ffb199)",
    "linear-gradient(135deg,#667eea,#764ba2)",
    "linear-gradient(135deg,#43cea2,#185a9d)",
    "linear-gradient(135deg,#f7971e,#ffd200)"
  ];

  const gradient =
    gradientColors[initials.charCodeAt(0) % gradientColors.length];

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;

    setForm((prev) => ({
      ...prev,
      [name]: type === "checkbox" ? checked : value,
    }));
  };
  const handleProfessionalChange = (e) => {
    const { name, value, type, checked } = e.target;
    setForm((current) => ({
      ...current,
      professionalData: {
        ...current.professionalData,
        [name]: type === "checkbox" ? checked : value,
      },
    }));
  };

  const handleAvatarUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;

    try {
      setUploading(true);
      const imageUrl = await uploadImage(file);

      setForm((prev) => ({
        ...prev,
        avatar: imageUrl,
      }));
    } catch (err) {
      console.error("Error subiendo imagen:", err);
    }

    setUploading(false);
  };

  const handlePresetSelect = (url) => {
    setForm((prev) => ({
      ...prev,
      avatar: url,
    }));
  };

  const handleRemoveAvatar = () => {
    setForm((prev) => ({
      ...prev,
      avatar: "",
    }));
  };

  const handleProfessionalImageUpload = async (event, field) => {
    const file = event.target.files?.[0];
    if (!file) return;
    try {
      setProfessionalUploading(field);
      const imageUrl = await uploadImage(file);
      setForm((current) => ({
        ...current,
        professionalData: { ...current.professionalData, [field]: imageUrl },
      }));
    } catch {
      setProfileFeedback("No pudimos subir esa imagen.");
    } finally {
      setProfessionalUploading("");
    }
  };

  /* ================= SAVE ================= */

  const handleSave = async () => {
    const whatsappPhone = form.phone || user?.professionalData?.phone || "";
    const whatsappDigits = whatsappPhone.replace(/\D/g, "");
    if (form.allowWhatsapp && (whatsappDigits.length < 10 || whatsappDigits.length > 15)) {
      setProfileFeedback("Agregá un teléfono válido con código de área para activar WhatsApp.");
      return;
    }
    setLoading(true);
    setProfileFeedback("");

    try {
      const payload = user?.role === "PROFESSIONAL" ? {
        ...form,
        professionalData: {
          ...form.professionalData,
          yearsExperience: Number(form.professionalData.yearsExperience || 0),
          specialties: form.professionalData.specialties.split(",").map(value => value.trim()).filter(Boolean),
          serviceAreas: form.professionalData.serviceAreas.split(",").map(value => value.trim()).filter(Boolean),
        },
      } : form;
      const updatedUser = await apiRequest('/users/me', {
        method: "PUT",
        body: payload,
      });
      setUser(updatedUser);
      setProfileFeedback("Tus datos se guardaron correctamente.");
      navigate("/client");
    } catch (err) {
      setProfileFeedback(err.message || "No pudimos guardar tus datos.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <Wrapper>
      <Card>
        <Header>
          <AvatarSection>
            <Avatar style={{ background: form.avatar ? "none" : gradient }}>
              {form.avatar ? (
                <>
                  <img src={form.avatar} alt="avatar" />
                  <RemoveAvatar onClick={handleRemoveAvatar}>✕</RemoveAvatar>
                </>
              ) : (
                <Initials>{initials}</Initials>
              )}
            </Avatar>

            <AvatarControls>
              <UploadLabel>
                {uploading ? "Subiendo..." : "Subir foto"}
                <input
                  type="file"
                  accept="image/*"
                  hidden
                  onChange={handleAvatarUpload}
                />
              </UploadLabel>

              <PresetGrid>
                {presetAvatars.map((src, i) => (
                  <Preset key={i} onClick={() => handlePresetSelect(src)}>
                    <img src={src} alt="preset" />
                  </Preset>
                ))}
              </PresetGrid>
            </AvatarControls>
          </AvatarSection>

          <HeaderText>
            <h1>Tu perfil</h1>
            <p>Completa tu información básica.</p>
          </HeaderText>
        </Header>

        <Grid>
          <Field>
            <Label>Nombre</Label>
            <Input
              name="name"
              value={form.name}
              onChange={handleChange}
              placeholder="Tu nombre"
            />
          </Field>

          <Field>
            <Label>Apellido</Label>
            <Input
              name="lastName"
              value={form.lastName}
              onChange={handleChange}
              placeholder="Tu apellido"
            />
          </Field>

          <Field>
            <Label>Email</Label>
            <DisabledInput value={user?.email || ""} disabled />
          </Field>

          <Field>
            <Label>Teléfono</Label>
            <Input
              name="phone"
              value={form.phone}
              onChange={handleChange}
              placeholder="+54 9 261 000 0000"
            />
          </Field>

          <Field>
            <Label>Ciudad</Label>
            <Input
              name="city"
              value={form.city}
              onChange={handleChange}
              placeholder="Mendoza"
            />
          </Field>

          <Field full>
            <WhatsAppChoice>
              <WhatsAppCopy>
                <Recommendation>RECOMENDADO</Recommendation>
                <strong>Recibí consultas por WhatsApp</strong>
                <span>Permití que las personas interesadas abran una conversación con vos desde tus publicaciones. Podés desactivarlo cuando quieras.</span>
                <small>Tu número solo se muestra como acceso de contacto cuando activás esta opción.</small>
              </WhatsAppCopy>
              <WhatsAppSwitch $active={form.allowWhatsapp}>
              <input
                type="checkbox"
                name="allowWhatsapp"
                checked={form.allowWhatsapp}
                onChange={handleChange}
              />
                <i />
                <span>{form.allowWhatsapp ? "Activado" : "Desactivado"}</span>
              </WhatsAppSwitch>
            </WhatsAppChoice>
          </Field>
        </Grid>

        {user?.role === "PROFESSIONAL" && (
          <ProfessionalSection>
            <SectionIntro>
              <div><ProfessionalKicker>PERFIL PROFESIONAL</ProfessionalKicker><h2>Hacé visible tu trayectoria.</h2></div>
              <p>Esta información aparece en tu perfil público y ayuda a que las personas conozcan quién está detrás de cada propiedad.</p>
            </SectionIntro>
            <BrandImages>
              <BrandUpload>
                <span>{form.professionalData.logo ? <img src={form.professionalData.logo} alt="" /> : "LOGO"}</span>
                <div><strong>Logo de la empresa</strong><small>Idealmente cuadrado y con fondo limpio.</small><label>{professionalUploading === "logo" ? "Subiendo…" : "Elegir logo"}<input type="file" accept="image/*" hidden onChange={event => handleProfessionalImageUpload(event, "logo")} /></label></div>
              </BrandUpload>
              <BrandUpload $cover>
                <span>{form.professionalData.cover ? <img src={form.professionalData.cover} alt="" /> : "PORTADA"}</span>
                <div><strong>Imagen de portada</strong><small>Una foto horizontal que represente la marca o su zona.</small><label>{professionalUploading === "cover" ? "Subiendo…" : "Elegir portada"}<input type="file" accept="image/*" hidden onChange={event => handleProfessionalImageUpload(event, "cover")} /></label></div>
              </BrandUpload>
            </BrandImages>
            <Grid>
              <Field><Label>Nombre de la empresa</Label><Input name="companyName" value={form.professionalData.companyName} onChange={handleProfessionalChange} placeholder="Inmobiliaria Andina" /></Field>
              <Field><Label>Años de experiencia</Label><Input name="yearsExperience" type="number" min="0" max="80" value={form.professionalData.yearsExperience} onChange={handleProfessionalChange} placeholder="12" /></Field>
              <Field><Label>Matrícula profesional</Label><Input name="license" value={form.professionalData.license} onChange={handleProfessionalChange} placeholder="CCPIM 0000" /></Field>
              <Field><Label>Jurisdicción</Label><Input name="licenseJurisdiction" value={form.professionalData.licenseJurisdiction} onChange={handleProfessionalChange} placeholder="Mendoza" /></Field>
              <Field full><Label>Presentación</Label><TextArea name="description" maxLength="2000" value={form.professionalData.description} onChange={handleProfessionalChange} placeholder="Contá cómo trabajan, qué los distingue y qué tipo de operaciones acompañan." /></Field>
              <Field full><Label>Especialidades</Label><Input name="specialties" value={form.professionalData.specialties} onChange={handleProfessionalChange} placeholder="Ventas, alquileres, desarrollos, propiedades rurales" /><FieldHint>Separalas con comas.</FieldHint></Field>
              <Field full><Label>Zonas donde trabaja</Label><Input name="serviceAreas" value={form.professionalData.serviceAreas} onChange={handleProfessionalChange} placeholder="Ciudad de Mendoza, Godoy Cruz, Luján de Cuyo" /><FieldHint>Separalas con comas.</FieldHint></Field>
              <Field><Label>Teléfono profesional</Label><Input name="phone" value={form.professionalData.phone} onChange={handleProfessionalChange} placeholder="+54 9 261 000 0000" /></Field>
              <Field><Label>Horario de atención</Label><Input name="businessHours" value={form.professionalData.businessHours} onChange={handleProfessionalChange} placeholder="Lun a vie · 9 a 18 h" /></Field>
              <Field><Label>Dirección</Label><Input name="address" value={form.professionalData.address} onChange={handleProfessionalChange} placeholder="Arístides Villanueva 000" /></Field>
              <Field><Label>Ciudad</Label><Input name="city" value={form.professionalData.city} onChange={handleProfessionalChange} placeholder="Mendoza" /></Field>
              <Field><Label>Provincia</Label><Input name="province" value={form.professionalData.province} onChange={handleProfessionalChange} placeholder="Mendoza" /></Field>
              <Field><Label>Sitio web</Label><Input name="website" value={form.professionalData.website} onChange={handleProfessionalChange} placeholder="https://..." /></Field>
              <Field><Label>Instagram</Label><Input name="instagram" value={form.professionalData.instagram} onChange={handleProfessionalChange} placeholder="@inmobiliaria" /></Field>
              <Field full><VisibilityChoices>
                <label><input type="checkbox" name="showPhone" checked={form.professionalData.showPhone} onChange={handleProfessionalChange} /><span><strong>Mostrar teléfono</strong><small>Las personas podrán verlo en tu perfil público.</small></span></label>
                <label><input type="checkbox" name="showAddress" checked={form.professionalData.showAddress} onChange={handleProfessionalChange} /><span><strong>Mostrar dirección</strong><small>Activala sólo si atendés al público allí.</small></span></label>
              </VisibilityChoices></Field>
            </Grid>
          </ProfessionalSection>
        )}

        <SaveRow>
          {profileFeedback && <ProfileFeedback role="status">{profileFeedback}</ProfileFeedback>}
          <SaveButton onClick={handleSave} disabled={loading}>
            {loading ? "Guardando..." : "Guardar cambios"}
          </SaveButton>
        </SaveRow>
      </Card>
      <SecurityCard>
        <SecurityHeader>
          <div><SecurityKicker>SEGURIDAD</SecurityKicker><h2>Dispositivos con acceso</h2><p>Revisá dónde está abierta tu cuenta y cerrá cualquier sesión que no reconozcas.</p></div>
          {sessions.length > 1 && <CloseOthers type="button" onClick={closeOtherSessions}>Cerrar las demás</CloseOthers>}
        </SecurityHeader>
        {sessionFeedback && <SessionFeedback role="status">{sessionFeedback}</SessionFeedback>}
        <SessionList>
          {sessions.map((session) => (
            <SessionRow key={session.id}>
              <DeviceMark>{session.current ? '✓' : '•'}</DeviceMark>
              <div>
                <strong>{session.device}</strong>
                <span>{session.current ? 'Este dispositivo · ' : ''}Última actividad {new Date(session.lastSeenAt).toLocaleString('es-AR', { dateStyle:'medium', timeStyle:'short' })}</span>
              </div>
              {session.current
                ? <CurrentBadge>Actual</CurrentBadge>
                : <CloseSession type="button" onClick={() => closeSession(session.id)}>Cerrar</CloseSession>}
            </SessionRow>
          ))}
          {!sessions.length && !sessionFeedback && <EmptySessions>Cargando dispositivos…</EmptySessions>}
        </SessionList>
      </SecurityCard>
      <DangerCard>
        <SecurityKicker>PRIVACIDAD Y DATOS</SecurityKicker>
        <h2>Eliminar mi cuenta</h2>
        <p>La baja elimina tu perfil, publicaciones, proyectos, favoritos, alertas, conversaciones y sesiones. Antes cancelaremos cualquier suscripción activa. Esta acción no se puede deshacer.</p>
        {deletionFeedback && <DeletionFeedback role="status">{deletionFeedback}</DeletionFeedback>}
        {deletionStep === "confirm" && <DeletionForm>
          <label>Código recibido por correo<Input value={deletionCode} maxLength={8} onChange={e=>setDeletionCode(e.target.value.toUpperCase())} placeholder="XXXXXXXX"/></label>
          <label>Escribí ELIMINAR para confirmar<Input value={deletionPhrase} onChange={e=>setDeletionPhrase(e.target.value.toUpperCase())} placeholder="ELIMINAR"/></label>
        </DeletionForm>}
        <DangerActions>
          {deletionStep === "idle" && <DeleteButton type="button" onClick={requestDeletion}>Solicitar eliminación</DeleteButton>}
          {deletionStep === "confirm" && <>
            <CancelDeletion type="button" onClick={()=>{setDeletionStep("idle");setDeletionCode("");setDeletionPhrase("");setDeletionFeedback("");}}>Cancelar</CancelDeletion>
            <DeleteButton type="button" disabled={!deletionCode || deletionPhrase !== "ELIMINAR"} onClick={confirmDeletion}>Eliminar definitivamente</DeleteButton>
          </>}
          {deletionStep === "deleting" && <DeleteButton disabled>Eliminando…</DeleteButton>}
        </DangerActions>
      </DangerCard>
    </Wrapper>
  );
};

export default PerfilAjustes;

/* ================= STYLES ================= */

const Wrapper = styled.div`
  padding: 140px 20px 100px;

  @media (max-width: 768px) {
    padding: 32px 16px 60px;
  }
`;

const Card = styled.div`
  max-width: 1100px;
  margin: 0 auto;
  background: white;
  border: 1px solid #dce4e3;
  border-radius: 4px;
  padding: 60px;
  box-shadow: 0 30px 80px rgba(42,64,70,0.07);

  @media (max-width: 768px) {
    padding: 28px 20px;
    border-radius: 18px;
  }
`;

const ProfessionalSection = styled.section`
  margin-top: 54px;
  padding-top: 50px;
  border-top: 1px solid #dce4e3;
`;

const SectionIntro = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 45px;
  align-items: end;
  margin-bottom: 34px;
  h2 { margin: 4px 0 0; color: #29474f; font-size: clamp(30px, 4vw, 48px); line-height: 1; letter-spacing: -.045em; }
  p { margin: 0; color: #708083; line-height: 1.65; }
  @media (max-width: 700px) { grid-template-columns: 1fr; gap: 15px; }
`;

const ProfessionalKicker = styled.span`
  color: #c66f4e;
  font-size:12px;
  font-weight: 850;
  letter-spacing: .16em;
`;

const TextArea = styled.textarea`
  width: 100%;
  min-height: 145px;
  box-sizing: border-box;
  resize: vertical;
  padding: 15px 16px;
  color: #29474f;
  background: #faf9f6;
  border: 1px solid #d7dfde;
  border-radius: 12px;
  font: inherit;
  line-height: 1.55;
  outline: none;
  &:focus { border-color: #c66f4e; box-shadow: 0 0 0 4px rgba(198,111,78,.08); }
`;

const FieldHint = styled.small`
  display: block;
  margin-top: 7px;
  color: #849092;
`;

const VisibilityChoices = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 12px;
  label { display: flex; align-items: flex-start; gap: 11px; padding: 17px; background: #f6f5f1; border: 1px solid #dce4e3; border-radius: 14px; cursor: pointer; }
  input { margin-top: 3px; accent-color: #c66f4e; }
  span, strong, small { display: block; }
  strong { color: #29474f; font-size: 14px; }
  small { margin-top: 4px; color: #7b898b; line-height: 1.4; }
  @media (max-width: 620px) { grid-template-columns: 1fr; }
`;

const BrandImages = styled.div`
  display: grid;
  grid-template-columns: .8fr 1.2fr;
  gap: 12px;
  margin-bottom: 30px;
  @media (max-width: 720px) { grid-template-columns: 1fr; }
`;

const BrandUpload = styled.div`
  display: grid;
  grid-template-columns: ${({$cover})=>$cover?'150px 1fr':'92px 1fr'};
  align-items: center;
  gap: 18px;
  padding: 16px;
  background: #f6f5f1;
  border: 1px solid #dce4e3;
  border-radius: 16px;
  > span { display: grid; width: 100%; height: 82px; place-items: center; overflow: hidden; color: #879395; background: #e6ebe8; border-radius: 12px; font-size:12px; font-weight: 800; letter-spacing: .12em; }
  img { width: 100%; height: 100%; object-fit: ${({$cover})=>$cover?'cover':'contain'}; }
  strong, small { display: block; }
  strong { color: #29474f; }
  small { margin: 4px 0 10px; color: #788789; line-height: 1.35; }
  label { color: #c66f4e; font-size: 12px; font-weight: 800; cursor: pointer; }
  @media(max-width:460px){grid-template-columns:76px 1fr;}
`;

const Header = styled.div`
  display: flex;
  gap: 60px;
  margin-bottom: 50px;

  @media (max-width: 768px) {
    flex-direction: column;
    gap: 20px;
    margin-bottom: 32px;
    text-align: center;
  }
`;

const AvatarSection = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
`;

const Avatar = styled.div`
  width: 140px;
  height: 140px;
  border-radius: 50%;
  overflow: visible; /* 👈 antes estaba hidden */
  display: flex;
  align-items: center;
  justify-content: center;
  position: relative; /* 👈 necesario para posicionar la X */

  img {
    width: 100%;
    height: 100%;
    object-fit: cover;
    border-radius: 50%;
  }
`;

const RemoveAvatar = styled.button`
  position: absolute;
  top: -6px;
  right: -6px;
  width: 28px;
  height: 28px;
  border-radius: 50%;
  background: white;
  border: 2px solid #c66f4e;
  color: #c66f4e;
  font-size: 14px;
  font-weight: bold;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 2;

  &:hover {
    background: #c66f4e;
    color: white;
  }
`;

const Initials = styled.div`
  font-size: 48px; /* 🔥 MÁS GRANDE */
  font-weight: 700;
  color: white;
`;

const AvatarControls = styled.div`
  margin-top: 15px;
  text-align: center;
`;

const UploadLabel = styled.label`
  font-size: 15px;
  font-weight: 600;
  color: #c66f4e;
  cursor: pointer;
`;

const PresetGrid = styled.div`
  display: flex;
  gap: 10px;
  margin-top: 12px;
`;

const Preset = styled.div`
  width: 40px;
  height: 40px;
  border-radius: 50%;
  overflow: hidden;
  cursor: pointer;
  border: 2px solid transparent;

  &:hover {
    border-color: #c66f4e;
  }

  img {
    width: 100%;
    height: 100%;
    object-fit: cover;
  }
`;

const HeaderText = styled.div`
  h1 {
    font-size: 28px;
    margin-bottom: 6px;
  }
  p {
    color: #666;
    font-size: 16px;
  }
`;

const Grid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(280px, 1fr));
  gap: 30px;

  @media (max-width: 640px) {
    grid-template-columns: minmax(0, 1fr);
    gap: 20px;
  }
`;

const Field = styled.div`
  display: flex;
  flex-direction: column;

  ${({ full }) =>
    full &&
    `
    grid-column: 1 / -1;
  `}
`;

const Label = styled.label`
  font-size: 15px; /* 🔥 más grande */
  font-weight: 600;
  margin-bottom: 10px;
`;

const Input = styled.input`
  padding: 18px;
  font-size: 18px; /* 🔥 placeholder más grande */
  border-radius: 2px;
  border: 1px solid #dce4e3;
  width: 100%;
  min-width: 0;

  &::placeholder {
    font-size: 18px;
    color: #aaa;
  }
`;

const DisabledInput = styled(Input)`
  background: #f5f5f5;
`;

const WhatsAppChoice = styled.div`
  display:flex;align-items:center;justify-content:space-between;gap:28px;padding:24px;
  border:1px solid rgba(42,64,70,.13);border-radius:18px;background:#f5f7f2;
  @media(max-width:640px){align-items:flex-start;flex-direction:column;padding:20px;}
`;
const WhatsAppCopy = styled.div`
  max-width:650px;strong,span,small{display:block;}strong{margin:9px 0 7px;color:#29474f;font-size:18px;}
  span{color:#657579;font-size:14px;line-height:1.55;}small{margin-top:9px;color:#84908f;font-size:12px;line-height:1.45;}
`;
const Recommendation = styled.b`
  color:#3f7259;font-size:12px;font-weight:850;letter-spacing:.14em;
`;
const WhatsAppSwitch = styled.label`
  display:flex;flex:0 0 auto;align-items:center;gap:10px;color:#52676c;font-size:12px;font-weight:750;cursor:pointer;
  input{position:absolute;opacity:0;pointer-events:none;}
  i{position:relative;width:48px;height:28px;border-radius:999px;background:${({$active})=>$active?'#4c7a63':'#cbd2ce'};transition:.2s;}
  i::after{content:'';position:absolute;top:4px;left:${({$active})=>$active?'24px':'4px'};width:20px;height:20px;border-radius:50%;background:#fff;box-shadow:0 2px 7px rgba(30,50,55,.2);transition:.2s;}
  input:focus-visible+i{outline:3px solid rgba(76,122,99,.2);outline-offset:3px;}
`;

const SaveRow = styled.div`
  margin-top: 50px;
  display: flex;
  align-items: center;
  gap: 16px;
  justify-content: flex-end;

  @media (max-width: 640px) {
    margin-top: 32px;
  }
`;
const ProfileFeedback = styled.span`
  color: #52676c;
  font-size: 13px;
  font-weight: 650;
`;

const SaveButton = styled.button`
  background: #c66f4e;
  color: white;
  padding: 16px 40px;
  border-radius: 999px;
  border: none;
  font-weight: 600;
  font-size: 16px;
  cursor: pointer;
  @media (max-width: 640px) {
    width: 100%;
    min-height: 48px;
  }
`;
const SecurityCard = styled.section`max-width:1100px;margin:24px auto 0;padding:34px 40px;background:#fff;border:1px solid #dce4e3;border-radius:18px;box-shadow:0 22px 60px rgba(42,64,70,.05);@media(max-width:768px){padding:24px 20px;}`;
const SecurityHeader = styled.header`display:flex;align-items:flex-start;justify-content:space-between;gap:24px;padding-bottom:24px;border-bottom:1px solid #e1e6e4;h2{margin:0 0 7px;color:#29474f;font-size:24px;}p{max-width:620px;margin:0;color:#718083;font-size:14px;line-height:1.55;}@media(max-width:640px){flex-direction:column;}`;
const SecurityKicker = styled.div`margin-bottom:8px;color:#c66f4e;font-size:12px;font-weight:800;letter-spacing:.16em;`;
const CloseOthers = styled.button`flex:0 0 auto;min-height:44px;padding:0 18px;color:#29474f;background:#eef1ed;border:0;border-radius:999px;font-weight:750;cursor:pointer;`;
const SessionFeedback = styled.div`margin:18px 0 0;padding:11px 14px;color:#29474f;background:#edf2ee;border-radius:10px;font-size:13px;`;
const SessionList = styled.div`display:grid;`;
const SessionRow = styled.div`display:grid;grid-template-columns:42px minmax(0,1fr) auto;align-items:center;gap:14px;padding:18px 0;border-bottom:1px solid #edf0ee;&:last-child{border-bottom:0;}strong,span{display:block;}strong{color:#29474f;font-size:14px;}span{margin-top:4px;color:#819092;font-size:12px;line-height:1.4;}`;
const DeviceMark = styled.i`display:grid;width:38px;height:38px;place-items:center;color:#fff;background:#29474f;border-radius:50%;font-style:normal;font-weight:800;`;
const CurrentBadge = styled.span`padding:7px 10px;color:#37705a!important;background:#e6f1ea;border-radius:999px;font-size:12px!important;font-weight:800;letter-spacing:.08em;text-transform:uppercase;`;
const CloseSession = styled.button`padding:8px 11px;color:#a64f35;background:transparent;border:1px solid rgba(166,79,53,.24);border-radius:999px;font-weight:750;cursor:pointer;`;
const EmptySessions = styled.div`padding:24px 0;color:#819092;font-size:13px;`;
const DangerCard=styled.section`max-width:1100px;margin:24px auto 0;padding:34px 40px;background:#fffaf8;border:1px solid rgba(166,79,53,.28);border-radius:18px;h2{margin:0 0 9px;color:#713823;font-size:24px}p{max-width:760px;margin:0;color:#7c6c67;font-size:14px;line-height:1.6}@media(max-width:768px){padding:24px 20px}`;
const DeletionFeedback=styled.div`margin-top:18px;padding:12px 14px;border-radius:10px;background:#fff0eb;color:#8f422e;font-size:13px;line-height:1.45`;
const DeletionForm=styled.div`display:grid;grid-template-columns:repeat(2,minmax(0,1fr));gap:16px;margin-top:20px;label{display:grid;gap:8px;color:#713823;font-size:13px;font-weight:750}@media(max-width:640px){grid-template-columns:1fr}`;
const DangerActions=styled.div`display:flex;justify-content:flex-end;gap:12px;margin-top:22px;@media(max-width:520px){flex-direction:column-reverse}`;
const DeleteButton=styled.button`min-height:48px;padding:0 22px;border:0;border-radius:999px;background:#a94f34;color:white;font-size:15px;font-weight:800;cursor:pointer;&:disabled{opacity:.5;cursor:not-allowed}`;
const CancelDeletion=styled.button`min-height:48px;padding:0 20px;border:1px solid #d8c8c2;border-radius:999px;background:white;color:#674e46;font-size:15px;font-weight:750;cursor:pointer`;
